/**
 * Native OAuth callback trampoline page — handles OAuth redirect for
 * desktop (Tauri) and mobile (Capacitor) apps.
 *
 * After Google/Microsoft OAuth completes, the browser redirects here.
 * This page extracts the platform from the OAuth state parameter and
 * redirects to the appropriate custom scheme to return to the native app:
 *   - Tauri desktop: panluma://auth/sso/callback?code=...&state=...
 *   - Capacitor mobile: ai.panluma.app://auth/sso/callback?code=...&state=...
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Loader2 } from 'lucide-react';

const CUSTOM_SCHEMES: Record<string, string> = {
  tauri: 'panluma://auth/sso/callback',
  capacitor: 'ai.panluma.app://auth/sso/callback',
};

/** Decode the platform from the OAuth state parameter.
 *  State format: "token" or "token|base64_json_payload"
 *  Payload may contain { platform: "tauri" | "capacitor", ... }
 */
function getPlatformFromState(state: string | null): string | null {
  if (!state || !state.includes('|')) return null;
  try {
    const payload = state.split('|', 2)[1];
    const standardBase64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(standardBase64);
    const parsed = JSON.parse(decoded);
    return parsed?.platform ?? null;
  } catch {
    return null;
  }
}

export function NativeCallbackPage() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['auth', 'common']);
  const [error, setError] = useState<string | null>(null);
  const [redirected, setRedirected] = useState(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(t('auth:sso.error'));
      return;
    }

    if (!code) {
      setError(t('auth:sso.nativeCallback.missingAuthCode', 'Missing authorization code'));
      return;
    }

    // Determine platform from state payload
    const platform = getPlatformFromState(state);
    const baseUrl = platform ? CUSTOM_SCHEMES[platform] : null;

    if (!baseUrl) {
      // Fallback: try Tauri scheme (desktop was the original user of this flow)
      const fallbackUrl = CUSTOM_SCHEMES.tauri;
      const params = new URLSearchParams();
      params.set('code', code);
      if (state) params.set('state', state);
      window.location.href = `${fallbackUrl}?${params.toString()}`;
      setRedirected(true);
      return;
    }

    // Build redirect URL with OAuth parameters
    const params = new URLSearchParams();
    params.set('code', code);
    if (state) params.set('state', state);

    window.location.href = `${baseUrl}?${params.toString()}`;
    setRedirected(true);
  }, [searchParams, t]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-[400px] w-full">
        <CardContent className="p-8 text-center space-y-6">
          {error ? (
            <div className="space-y-4 text-left">
              <Alert variant="destructive">
                <AlertTitle>{t('common:app.error')}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                {t('auth:sso.nativeCallback.tryAgainFromApp', 'Please try again from the application.')}
              </p>
            </div>
          ) : !redirected ? (
            <div className="space-y-4">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground">{t('auth:sso.connecting')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">{t('auth:sso.nativeCallback.authComplete', 'Authentication complete')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('auth:sso.nativeCallback.returnToApp', 'You can return to the application now.')}
              </p>
              <Button
                onClick={() => window.close()}
                className="w-full"
              >
                {t('auth:sso.nativeCallback.closeWindow', 'Close this window')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
